import React, { ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { Input } from '@components/input/src';
import { NestedCheckboxList } from '@components/nested-checkbox-list';
import { getChildrenIds } from '@components/nested-checkbox-list/utils';
import { Transition } from '@headlessui/react';
import { useOnClickOutside } from '@hooks/use-click-outside';
import classNames from 'classnames';

import { InputSelectNestedCheckboxProps } from './input-select-nested-checkbox.type';

export const InputSelectNestedCheckbox = forwardRef(
  (
    {
      data,
      onChange,
      clearable,
      clearText,
      emptySelectText,
      selectedIds,
      canSelectAll,
      selectAllText,
      selectAllId,
      canSearch,
      searchText,
      disabled,
      listHeightClassName = 'h-96',
    }: InputSelectNestedCheckboxProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [readableValue, setReadableValue] = useState(emptySelectText);
    const [count, setCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const handleClickInside = () => {
      if (disabled) return;
      setIsOpen(!isOpen);
    };

    const handleClickOutside = () => {
      setIsOpen(false);
    };

    useOnClickOutside(wrapperRef, handleClickOutside);

    const createValue = useCallback(
      (ids: string[]) => {
        if (ids.length === 0) {
          setReadableValue(emptySelectText);
          return;
        }
        const nameMap = new Map<string, string>();
        data.forEach((item) => {
          if (item.children.length > 0) {
            item.children.forEach((child) => nameMap.set(child.id, child.name));
          } else {
            nameMap.set(item.id, item.name);
          }
        });
        const resolvedNames = ids.map((id) => nameMap.get(id)).filter(Boolean);
        setReadableValue(`(${resolvedNames.length}) ${resolvedNames.join(', ')}`);
      },
      [data, emptySelectText]
    );

    const handleChange = (ids: string[]) => onChange(ids);

    useEffect(() => {
      const idsWithoutParent = getChildrenIds(selectedIds, data);
      createValue(idsWithoutParent);
      setCount(idsWithoutParent.length);
    }, [selectedIds, createValue, data]);

    return (
      <div className='relative w-full' ref={wrapperRef}>
        <Input
          type='text'
          className={classNames('relative z-10 truncate flex grow transition-all', {
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled,
          })}
          value={readableValue}
          readOnly
          disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          onClick={handleClickInside}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleClickInside();
            }
          }}
          ref={inputRef}
          isChangingInactiveColor={count === 0}
        />
        <Transition
          appear={true}
          enter='transition duration-200 ease-in-out'
          enterFrom='-translate-y-10 opacity-0'
          enterTo='translate-y-0 opacity-100'
          leave='transition duration-125 ease-in-out'
          leaveFrom='translate-y-0 opacity-100'
          leaveTo='-translate-y-10 opacity-0'
          show={isOpen}>
          <div
            className={classNames(
              'absolute z-2 top-full w-full my-4 rounded-b-lg bg-white shadow-sm  overflow-y-auto scrollbar',
              listHeightClassName
            )}
            ref={ref}>
            <NestedCheckboxList
              data={data}
              selectedIds={selectedIds}
              clearable={clearable}
              clearText={clearText}
              selectAllId={selectAllId}
              selectAllText={selectAllText}
              canSelectAll={canSelectAll}
              canSearch={canSearch}
              searchText={searchText}
              onChange={handleChange}
            />
          </div>
        </Transition>
      </div>
    );
  }
);
